import { useState, useEffect } from "react";
import { useNavigate } from "@/lib/router-compat";
import { Gamepad2, Users, Trophy, Loader2 } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { gameService, Game } from "@/services/gameService";
import { toast } from "sonner";
import { PageHeader } from "@/components/ui/page-header";
import { PageShell } from "@/components/layout/PageShell";
import { EmptyState } from "@/components/ui/empty-state";
import { T } from "@/i18n/T";
import { useT } from "@/i18n/useLocale";


const Games = () => {
  const t = useT();
  const navigate = useNavigate();
  const [games, setGames] = useState<Game[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadGames();
  }, []);

  const loadGames = async () => {
    try {
      const data = await gameService.getGames();
      setGames(data);
    } catch (error) {
      console.error("Error loading games:", error);
      toast.error("Failed to load games");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <PageShell className="space-y-8">
      <PageHeader
        align="center"
        eyebrow={t("auto.multiplayer-901a")}
        title={t("auto.games-251b")}
        description={t("auto.play-multiplayer-games-with-the-byteline-57d0")}
      />



      <div className="grid grid-cols-3 gap-3 md:gap-4 max-w-lg mx-auto">
        <Card className="text-center">
          <CardContent className="pt-4">
            <Gamepad2 className="h-6 w-6 mx-auto mb-2 text-primary" />
            <p className="text-2xl font-bold">{games.length}</p>
            <p className="text-xs text-muted-foreground"><T k="auto.games-398e" /></p>
          </CardContent>
        </Card>
        <Card className="text-center">
          <CardContent className="pt-4">
            <Users className="h-6 w-6 mx-auto mb-2 text-success" />
            <p className="text-2xl font-bold">8</p>
            <p className="text-xs text-muted-foreground"><T k="auto.max-players-77c3" /></p>
          </CardContent>
        </Card>
        <Card className="text-center">
          <CardContent className="pt-4">
            <Trophy className="h-6 w-6 mx-auto mb-2 text-rank-gold" />
            <p className="text-2xl font-bold">100</p>
            <p className="text-xs text-muted-foreground"><T k="auto.win-xp-ff81" /></p>
          </CardContent>
        </Card>
      </div>

      {games.length === 0 ? (
        <EmptyState icon={Gamepad2} title={t("auto.no-games-available-4e8d")} description={t("auto.check-back-soon-for-new-games-4152")} />
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {games.map((game) => (
            <Card 
              key={game.id} 
              className="group hover:shadow-lg transition-all duration-300 overflow-hidden cursor-pointer"
              onClick={() => navigate(`/games/${game.slug}`)}
            >
              <div className="aspect-video bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center relative overflow-hidden">
                {game.thumbnail_url ? (
                  <img 
                    src={game.thumbnail_url} 
                    alt={game.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  loading="lazy" decoding="async" />
                ) : (
                  <Gamepad2 className="h-16 w-16 text-primary/50 group-hover:scale-110 transition-transform" />
                )}
                <div className="absolute top-3 right-3">
                  <Badge variant="secondary" className="bg-background/80 backdrop-blur">
                    {game.min_players}-{game.max_players} players
                  </Badge>
                </div>
              </div>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  {game.name}
                </CardTitle>
                <CardDescription className="line-clamp-2">
                  {game.description || "A fun multiplayer game!"}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4 text-sm text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <Trophy className="h-4 w-4 text-rank-gold" />
                      +{game.xp_reward} XP
                    </span>
                    <span className="text-success">
                      +{game.win_xp_bonus} win bonus
                    </span>
                  </div>
                  <Button size="sm">
                    <T k="auto.play-de3c" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </PageShell>

  );
};

export default Games;
